import React, { useState, useEffect } from 'react';
import { adminApi, disputesApi } from '../../lib/api';
import { Button } from '../../components/ui/button';
import { Badge } from '../../components/ui/badge';
import { Textarea } from '../../components/ui/textarea';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../../components/ui/tabs';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from '../../components/ui/dialog';
import { AlertTriangle, Check, Loader2, MessageSquare } from 'lucide-react';
import { formatDate } from '../../lib/utils';
import { toast } from 'sonner';

export default function AdminDisputes() {
  const [disputes, setDisputes] = useState([]);
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState('open');
  const [selectedDispute, setSelectedDispute] = useState(null);
  const [resolution, setResolution] = useState('');
  const [processing, setProcessing] = useState(false);

  useEffect(() => {
    fetchDisputes();
  }, []);

  const fetchDisputes = async () => {
    try {
      const response = await disputesApi.getAll();
      setDisputes(response.data);
    } catch (error) {
      console.error('Error fetching disputes:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleResolve = async () => {
    if (!resolution.trim()) {
      toast.error('Please provide a resolution');
      return;
    }

    setProcessing(true);
    try {
      await disputesApi.resolve(selectedDispute.id, resolution);
      toast.success('Dispute resolved');
      setSelectedDispute(null);
      setResolution('');
      fetchDisputes();
    } catch (error) {
      toast.error('Failed to resolve dispute');
    } finally {
      setProcessing(false);
    }
  };

  const filterDisputes = (status) => {
    if (status === 'open') {
      return disputes.filter((d) => ['open', 'under_review'].includes(d.status));
    }
    return disputes.filter((d) => ['resolved', 'closed'].includes(d.status));
  };

  const getStatusBadge = (status) => {
    const colors = {
      open: 'bg-farmhouse-clay/20 text-farmhouse-clay',
      under_review: 'bg-farmhouse-gold/20 text-farmhouse-gold',
      resolved: 'bg-farmhouse-moss/20 text-green-400',
      closed: 'bg-farmhouse-stone/20 text-farmhouse-muted',
    };
    return colors[status] || colors.open;
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-farmhouse-charcoal flex items-center justify-center">
        <Loader2 className="w-8 h-8 text-farmhouse-saddle animate-spin" />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-farmhouse-charcoal pt-8 pb-16" data-testid="admin-disputes-page">
      <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8">
        <h1 className="font-serif text-4xl font-semibold text-farmhouse-cream mb-8">
          Disputes
        </h1>

        <Tabs value={activeTab} onValueChange={setActiveTab}>
          <TabsList className="bg-farmhouse-paper border border-farmhouse-subtle mb-6">
            <TabsTrigger 
              value="open"
              className="data-[state=active]:bg-farmhouse-saddle data-[state=active]:text-white"
            >
              Open ({filterDisputes('open').length})
            </TabsTrigger>
            <TabsTrigger 
              value="resolved"
              className="data-[state=active]:bg-farmhouse-saddle data-[state=active]:text-white"
            >
              Resolved ({filterDisputes('resolved').length})
            </TabsTrigger>
          </TabsList>

          {['open', 'resolved'].map((tab) => (
            <TabsContent key={tab} value={tab}>
              {filterDisputes(tab).length > 0 ? (
                <div className="space-y-4">
                  {filterDisputes(tab).map((dispute) => (
                    <div
                      key={dispute.id}
                      className="bg-farmhouse-paper border border-farmhouse-subtle rounded-md p-4 cursor-pointer card-hover"
                      onClick={() => setSelectedDispute(dispute)}
                      data-testid={`dispute-${dispute.id}`}
                    >
                      <div className="flex items-start justify-between">
                        <div className="flex-1">
                          <div className="flex items-center gap-2 mb-2">
                            <Badge className={`${getStatusBadge(dispute.status)} border-0 capitalize`}>
                              {dispute.status.replace('_', ' ')}
                            </Badge>
                            <span className="text-farmhouse-muted text-sm">
                              {formatDate(dispute.created_at)}
                            </span>
                          </div>
                          <h3 className="font-medium text-farmhouse-cream mb-1">
                            {dispute.reason}
                          </h3>
                          <p className="text-farmhouse-muted text-sm line-clamp-2">
                            {dispute.description}
                          </p>
                        </div>
                        {tab === 'open' && (
                          <Button
                            size="sm"
                            onClick={(e) => {
                              e.stopPropagation();
                              setSelectedDispute(dispute);
                            }}
                            className="btn-primary shrink-0"
                            data-testid="resolve-dispute"
                          >
                            Resolve
                          </Button>
                        )}
                      </div>
                    </div>
                  ))}
                </div>
              ) : (
                <div className="text-center py-16 bg-farmhouse-paper border border-farmhouse-subtle rounded-md">
                  <AlertTriangle className="w-16 h-16 mx-auto mb-4 text-farmhouse-subtle" />
                  <h3 className="font-serif text-xl font-semibold text-farmhouse-cream mb-2">
                    No {tab} disputes
                  </h3>
                  <p className="text-farmhouse-muted">
                    {tab === 'open' ? 'All caught up! No disputes need attention.' : 'No resolved disputes yet.'}
                  </p>
                </div>
              )}
            </TabsContent>
          ))}
        </Tabs>

        {/* Resolve Dialog */}
        <Dialog open={!!selectedDispute} onOpenChange={(open) => !open && setSelectedDispute(null)}>
          <DialogContent className="bg-farmhouse-paper border-farmhouse-subtle max-w-lg">
            {selectedDispute && (
              <>
                <DialogHeader>
                  <DialogTitle className="font-serif text-2xl text-farmhouse-cream">
                    Dispute Details
                  </DialogTitle>
                </DialogHeader>
                
                <div className="space-y-4 mt-4">
                  <div>
                    <Badge className={`${getStatusBadge(selectedDispute.status)} border-0 capitalize`}>
                      {selectedDispute.status.replace('_', ' ')}
                    </Badge>
                  </div>

                  <div>
                    <p className="text-farmhouse-muted text-sm mb-1">Reason</p>
                    <p className="text-farmhouse-cream font-medium">{selectedDispute.reason}</p>
                  </div>

                  <div>
                    <p className="text-farmhouse-muted text-sm mb-1">Description</p>
                    <p className="text-farmhouse-cream">{selectedDispute.description}</p>
                  </div>

                  <div className="grid grid-cols-2 gap-4 p-4 bg-farmhouse-charcoal rounded-md">
                    <div>
                      <p className="text-farmhouse-muted text-sm">Opened</p>
                      <p className="text-farmhouse-cream">{formatDate(selectedDispute.created_at)}</p>
                    </div>
                    <div>
                      <p className="text-farmhouse-muted text-sm">Booking ID</p>
                      <p className="text-farmhouse-cream font-mono text-sm">{selectedDispute.booking_id}</p>
                    </div>
                  </div>

                  {selectedDispute.resolution && (
                    <div className="p-4 bg-farmhouse-moss/10 border border-farmhouse-moss/30 rounded-md">
                      <p className="text-farmhouse-muted text-sm mb-1">Resolution</p>
                      <p className="text-farmhouse-cream">{selectedDispute.resolution}</p>
                      {selectedDispute.resolved_at && (
                        <p className="text-farmhouse-muted text-sm mt-2">
                          Resolved on {formatDate(selectedDispute.resolved_at)}
                        </p>
                      )}
                    </div>
                  )}

                  {['open', 'under_review'].includes(selectedDispute.status) && (
                    <>
                      <div>
                        <label className="text-farmhouse-cream mb-2 block">Resolution</label>
                        <Textarea
                          placeholder="Describe the resolution..."
                          value={resolution}
                          onChange={(e) => setResolution(e.target.value)}
                          className="form-input min-h-[100px]"
                        />
                      </div>

                      <Button
                        onClick={handleResolve}
                        className="w-full bg-farmhouse-moss hover:bg-farmhouse-moss/80 text-white"
                        disabled={processing || !resolution.trim()}
                      >
                        {processing ? (
                          <Loader2 className="w-5 h-5 animate-spin" />
                        ) : (
                          <>
                            <Check className="w-4 h-4 mr-2" />
                            Mark as Resolved
                          </>
                        )}
                      </Button>
                    </>
                  )}
                </div>
              </>
            )}
          </DialogContent>
        </Dialog>
      </div>
    </div>
  );
}
